import React, { useState } from "react";
import { useLocation, NavLink } from "react-router-dom";
import {
  Image,
  FileText,
  Code,
  MessageSquare,
  Inbox,
  BookOpen,
  Webhook,
  Cpu,
  Globe,
  Zap,
  Database,
  Eye,
  BarChart,
  DollarSign,
  Smile,
  Building2,
  Megaphone,
  GitBranch,
  Users,
  LogOut,
  Clock,
  HelpCircle,
} from "lucide-react";

// Chevron Icon for dropdown arrows and collapsible headers
const IconChevron = ({ open }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="transition-transform duration-200"
    style={{
      transform: open ? "rotate(180deg)" : "rotate(0deg)",
    }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const Sidebar = ({ activeTab, setActiveTab, isCollapsed, setIsCollapsed, activeChatsCount = 0 }) => {
  const location = useLocation();

  // Get user role from session
  const userStr = sessionStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;
  const isAdmin = user?.role !== 'agent';

  // Grouped sections matching the user's screenshot layout and dropdown nested behavior
  const sections = [
    {
      title: "", // Top uncategorized section
      items: [
        { id: "agent-chat", label: "Live Chat", icon: MessageSquare, path: "/agent-chat", type: "single", hasBadge: true }
      ]
    },
    {
      title: "BOT BUILDER",
      items: [
        { id: "instructions", label: "Agent Instructions", icon: MessageSquare, path: "/instructions", type: "single" },
        { id: "business-flows", label: "Business Flows", icon: GitBranch, path: "/business-flows", type: "single" },
        { id: "api-tools", label: "API Tools", icon: Code, path: "/api-tools", type: "single" },
        {
          id: "sources",
          label: "Sources",
          icon: Database,
          type: "dropdown",
          subItems: [
            { id: "documents", label: "Knowledge Base", path: "/documents", icon: FileText },
            { id: "smart-faq", label: "Smart FAQ Builder", path: "/smart-faq", icon: BookOpen }
          ]
        }
      ]
    },
    {
      title: "COMMUNICATION",
      items: [
        { id: "chat", label: "Copilot", icon: Inbox, path: "/chat", type: "single" },
        { id: "campaigns", label: "Campaigns", icon: Megaphone, path: "/campaigns", type: "single" },
        { id: "contacts", label: "Contacts", icon: Users, path: "/contacts", type: "single" }
      ]
    },
    {
      title: "ANALYTICS",
      items: [
        { id: "analytics-overview", label: "Overview", path: "/analytics?tab=overview", icon: BarChart, type: "single" },
        { id: "analytics-tokens", label: "Tokens", path: "/analytics?tab=token-analytics", icon: Cpu, type: "single" },
        { id: "analytics-cost", label: "Cost Breakdown", path: "/analytics?tab=cost-analytics", icon: DollarSign, type: "single" },
        { id: "analytics-sessions", label: "Sessions", path: "/analytics?tab=sessions", icon: Clock, type: "single" },
        { id: "analytics-conversations", label: "Conversations", path: "/analytics?tab=conversations", icon: MessageSquare, type: "single" },
        { id: "analytics-sentiment", label: "Sentiment", path: "/analytics?tab=sentiment", icon: Smile, type: "single" }
      ]
    },
    {
      title: "SETTINGS",
      items: [
        {
          id: "workspace",
          label: "Workspace",
          icon: Building2,
          type: "dropdown",
          subItems: [
            { id: "agents", label: "Agents", path: "/agents", icon: Building2 },
            { id: "logo", label: "Logo Management", path: "/logo", icon: Image },
            { id: "domains", label: "Domain Management", path: "/domains", icon: Globe },
            { id: "widget", label: "Widget Integration", path: "/widget", icon: Code },
            { id: "widget-preview", label: "Widget Preview", path: "/widget-preview", icon: Eye }
          ]
        },
        { id: "user-management", label: "Teams & Roles", icon: Users, path: "/user-management", type: "single" },
        { id: "billing", label: "Billing & Plans", icon: DollarSign, path: "/billing", type: "single" },
        { id: "integrations", label: "Integrations", icon: Zap, path: "/integrations", type: "single" },
        { id: "models", label: "Models", icon: Cpu, path: "/models", type: "single" },
        { id: "webhooks", label: "Webhooks", icon: Webhook, path: "/webhooks", type: "single" }
      ]
    },
    {
      title: "SUPPORT",
      items: [
        { id: "docs", label: "Help Center", icon: HelpCircle, path: "/docs", type: "single" }
      ]
    }
  ];

  // RBAC filter: Agents only see the Live Chat route
  const filteredSections = sections.map(section => {
    return {
      ...section,
      items: section.items.filter(item => {
        if (!isAdmin) {
          return item.id === "agent-chat";
        }
        return true;
      })
    };
  }).filter(section => section.items.length > 0);

  // Initialize Collapsible Section states (with auto-expansion of current active section)
  const [expandedSections, setExpandedSections] = useState(() => {
    const initial = {
      "BOT BUILDER": true,
      "COMMUNICATION": true,
      "ANALYTICS": true,
      "SETTINGS": true,
      "SUPPORT": true
    };
    
    sections.forEach(sec => {
      if (sec.title) {
        const hasActiveItem = sec.items.some(item => {
          if (item.type === "single") {
            return location.pathname === item.path || (item.path && item.path.includes("?") && location.pathname + location.search === item.path);
          }
          if (item.type === "dropdown") {
            return item.subItems.some(sub => location.pathname === sub.path);
          }
          return false;
        });
        if (hasActiveItem) {
          initial[sec.title] = true;
        }
      }
    });
    
    return initial;
  });

  const toggleSection = (title) => {
    if (!title) return;
    setExpandedSections(prev => ({
      ...prev,
      [title]: !prev[title]
    }));
  };

  // Dropdown States for nested menus inside Bot Builder and Settings
  const isSourcesActive = filteredSections.find(s => s.title === "BOT BUILDER")?.items
    .find(i => i.id === "sources")?.subItems?.some(sub => location.pathname === sub.path);
  const [sourcesOpen, setSourcesOpen] = useState(!!isSourcesActive);

  const isWorkspaceActive = filteredSections.find(s => s.title === "SETTINGS")?.items
    .find(i => i.id === "workspace")?.subItems?.some(sub => location.pathname === sub.path);
  const [workspaceOpen, setWorkspaceOpen] = useState(!!isWorkspaceActive);

  const toggleDropdown = (id) => {
    if (id === "sources") setSourcesOpen(!sourcesOpen);
    if (id === "workspace") setWorkspaceOpen(!workspaceOpen);
  };

  const isDropdownOpen = (id) => {
    if (id === "sources") return sourcesOpen;
    if (id === "workspace") return workspaceOpen;
    return false;
  };

  // Helper check for active sub-items with exact search/query matching
  const isPathActive = (path) => {
    if (!path) return false;
    return location.pathname + location.search === path || 
      (path === "/analytics?tab=overview" && location.pathname === "/analytics" && !location.search);
  };

  return (
    <aside
      className={`${isCollapsed ? "w-20" : "w-56"} flex flex-col bg-white border-r border-gray-200 shrink-0 transition-all duration-300 shadow-sm`}
    >
      {/* Original Image Logo Header */}
      <div className={`h-[72px] border-b border-gray-100 flex items-center shrink-0 px-4 ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
        {!isCollapsed && (
          <div className="flex items-center transition-all duration-300 w-48">
            <NavLink to="/admin" className="cursor-pointer">
              <img
                src="/logo.png"
                alt="EasyChat"
                className="h-18 w-auto object-contain transition-all duration-300"
              />
            </NavLink>
          </div>
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
          title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-700">
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>
      </div>

      {/* Navigation list */}
      <nav className="flex-1 px-2.5 py-3 overflow-y-auto overflow-x-hidden space-y-4 custom-scrollbar">
        {filteredSections.map((section, secIdx) => {
          const isSectionExpanded = !section.title || expandedSections[section.title];

          return (
            <div key={secIdx} className="space-y-1">
              {/* Collapsible Section Title */}
              {section.title && (
                isCollapsed ? (
                  <div className="w-full flex items-center justify-center h-6 mb-0.5" key={section.title + "-divider"}>
                    <div className="w-8 border-t border-gray-200" />
                  </div>
                ) : (
                  <button
                    onClick={() => toggleSection(section.title)}
                    className="w-full flex items-center justify-between text-[9px] font-bold text-gray-400 uppercase tracking-widest px-4 h-6 rounded-lg hover:bg-gray-50/50 hover:text-gray-900 transition-all duration-200 mb-0.5"
                  >
                    <span>{section.title}</span>
                    <span className="text-gray-400">
                      <IconChevron open={isSectionExpanded} />
                    </span>
                  </button>
                )
              )}

              {/* Collapsible Section Items Container */}
              <div
                style={{
                  maxHeight: isSectionExpanded ? "2000px" : "0px",
                  overflow: "hidden",
                  transition: "max-height 0.25s ease-in-out",
                }}
                className="space-y-0.5"
              >
                {section.items.map((item) => {
                  const { id, label, path, icon: Icon, type, hasBadge, subItems } = item;

                  if (type === "single") {
                    return (
                      <NavLink
                        key={id}
                        to={path}
                        onClick={() => {
                          setActiveTab(id);
                          if (id === "contacts" && setIsCollapsed) {
                            setIsCollapsed(true);
                          }
                        }}
                        className={({ isActive }) => {
                          const effectiveActive = (path && path.includes("?")) ? isPathActive(path) : isActive;
                          return `group relative flex items-center min-h-[34px] rounded-[10px] text-xs font-semibold transition-all duration-200 mb-0.5 px-4 ${effectiveActive
                            ? "bg-[#83bbe1] text-white shadow-md shadow-blue-600/10"
                            : "text-gray-600 hover:text-gray-900 hover:bg-blue-50/50"
                            }`;
                        }}
                      >
                        {({ isActive }) => {
                          const effectiveActive = (path && path.includes("?")) ? isPathActive(path) : isActive;
                          return (
                            <div className="flex items-center w-full min-w-0">
                              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                                <Icon
                                  size={15}
                                  className={`transition-colors ${effectiveActive ? "text-white" : "text-gray-500 group-hover:text-gray-700"}`}
                                />
                              </div>
                              <div className={`flex-1 flex items-center justify-between overflow-hidden transition-all duration-300 ${
                                isCollapsed ? "w-0 opacity-0 ml-0 pointer-events-none" : "w-full opacity-100 ml-3"
                              }`}>
                                <span className="truncate">{label}</span>
                                {hasBadge && activeChatsCount > 0 && (
                                  <span className={`flex items-center justify-center px-1 py-0.5 text-[9px] font-bold rounded-full min-w-[16px] ml-2 ${effectiveActive ? "bg-white text-[#83bbe1]" : "bg-[#83bbe1] text-white"}`}>
                                    {activeChatsCount}
                                  </span>
                                )}
                              </div>
                              {/* Live count notification badge in collapsed state */}
                              {isCollapsed && hasBadge && activeChatsCount > 0 && (
                                <div className="absolute right-3.5 w-1.5 h-1.5 bg-[#83bbe1] rounded-full" />
                              )}
                            </div>
                          );
                        }}
                      </NavLink>
                    );
                  }

                  if (type === "dropdown") {
                    const isOpen = isDropdownOpen(id);
                    const dropdownActive = subItems.some(sub => location.pathname === sub.path);

                    return (
                      <React.Fragment key={id}>
                        <button
                          onClick={() => {
                            if (isCollapsed) {
                              if (setIsCollapsed) {
                                setIsCollapsed(false);
                              }
                              if (id === "sources") {
                                setSourcesOpen(true);
                                setExpandedSections(prev => ({ ...prev, "BOT BUILDER": true }));
                              }
                              if (id === "workspace") {
                                setWorkspaceOpen(true);
                                setExpandedSections(prev => ({ ...prev, "SETTINGS": true }));
                              }
                            } else {
                              toggleDropdown(id);
                            }
                          }}
                          className={`group relative w-full flex items-center min-h-[34px] rounded-[10px] text-xs font-semibold transition-all duration-200 mb-0.5 px-4 ${dropdownActive
                            ? "bg-[#83bbe1] text-white shadow-md shadow-blue-600/10"
                            : "text-gray-600 hover:text-gray-900 hover:bg-blue-50/50"
                            }`}
                        >
                          <div className="flex items-center w-full min-w-0">
                            <div className="w-5 h-5 flex items-center justify-center shrink-0">
                              <Icon
                                size={15}
                                className={`transition-colors ${dropdownActive ? "text-white" : "text-gray-500 group-hover:text-gray-700"}`}
                              />
                            </div>
                            <div className={`flex-1 flex items-center justify-between overflow-hidden transition-all duration-300 ${
                              isCollapsed ? "w-0 opacity-0 ml-0 pointer-events-none" : "w-full opacity-100 ml-3"
                            }`}>
                              <span className="truncate">{label}</span>
                              <span className={`${dropdownActive ? "text-white" : "text-gray-400 group-hover:text-gray-600"} ml-2 shrink-0`}>
                                <IconChevron open={isOpen} />
                              </span>
                            </div>
                          </div>
                        </button>

                        {/* Submenu links */}
                        {!isCollapsed && isOpen && (
                          <div className="space-y-0.5 mt-0.5">
                            {subItems.map((subItem) => {
                              const subActive = isPathActive(subItem.path);
                              return (
                                <NavLink
                                  key={subItem.id}
                                  to={subItem.path}
                                  onClick={() => setActiveTab(subItem.id)}
                                  className={({ isActive }) => {
                                    const effectiveActive = isActive || subActive;
                                    return `group relative flex items-center min-h-[34px] rounded-[10px] text-xs font-semibold transition-all duration-200 mb-0.5 px-4 ${effectiveActive
                                      ? "bg-[#83bbe1] text-white shadow-md shadow-blue-600/10"
                                      : "text-gray-600 hover:text-gray-900 hover:bg-blue-50/50"
                                      }`;
                                  }}
                                >
                                  {({ isActive }) => {
                                    const effectiveActive = isActive || subActive;
                                    return (
                                      <div className="flex items-center w-full min-w-0">
                                        <div className="w-5 h-5 flex items-center justify-center shrink-0">
                                          <subItem.icon
                                            size={15}
                                            className={`transition-colors ${effectiveActive ? "text-white" : "text-gray-500 group-hover:text-gray-700"}`}
                                          />
                                        </div>
                                        <div className="flex-1 flex items-center justify-between overflow-hidden ml-3">
                                          <span className="truncate">{subItem.label}</span>
                                        </div>
                                      </div>
                                    );
                                  }}
                                </NavLink>
                              );
                            })}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  }

                  return null;
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* Reverted Footer: Original Sign Out Button */}
      <div className="border-t border-gray-100 px-2.5 py-2">
        <button
          onClick={() => {
            sessionStorage.removeItem("user");
            window.location.href = "/login";
          }}
          className={`w-full flex items-center min-h-[34px] rounded-[10px] text-xs font-semibold text-gray-600 hover:text-white hover:bg-[#83bbe1] transition-colors duration-150 group px-4`}
        >
          <div className="w-5 h-5 flex items-center justify-center shrink-0">
            <LogOut
              size={14}
              className="text-gray-900 group-hover:text-white transition-colors"
            />
          </div>
          <div className={`flex items-center whitespace-nowrap overflow-hidden transition-all duration-300 ${isCollapsed ? "w-0 opacity-0" : "w-full opacity-100 ml-3"}`}>
            Sign Out
          </div>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
